import { cva, type VariantProps } from "class-variance-authority";
import { AlertTriangle, CheckCircle2, Info, OctagonAlert } from "lucide-react";
import { Children, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "../lib/cn";

export const badgeVariants = cva("inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap [&_svg]:size-3", {
  variants: {
    tone: {
      neutral: "bg-surface-2 text-fg",
      primary: "bg-primary-soft text-primary",
      success: "bg-success-soft text-success",
      warning: "bg-warning-soft text-warning",
      danger: "bg-danger-soft text-danger",
      info: "bg-info-soft text-info",
    },
  },
  defaultVariants: { tone: "neutral" },
});

/** Статусы: всегда текст + цвет (не только цвет — WCAG). */
export function Badge({ tone, className, dot, children }: VariantProps<typeof badgeVariants> & { className?: string; dot?: boolean; children: ReactNode }) {
  return (
    <span className={cn(badgeVariants({ tone }), className)}>
      {dot && <span className="size-1.5 shrink-0 rounded-full bg-current" aria-hidden />}
      {/* Текст длиннее места (узкий экран) — обрезается с многоточием, а не вылезает за рамку */}
      {Children.toArray(children).every((c) => typeof c === "string" || typeof c === "number") ? <span className="min-w-0 truncate">{children}</span> : children}
    </span>
  );
}

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("rounded-card border border-border bg-surface", className)} {...props} />;
}

export function CardHeader({ title, description, actions, className }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-wrap items-start justify-between gap-3 p-4 sm:p-5", className)}>
      <div className="min-w-0">
        <h3 className="font-semibold">{title}</h3>
        {description && <p className="mt-0.5 text-sm text-muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

const progressTone = { primary: "bg-primary", success: "bg-success", warning: "bg-warning", danger: "bg-danger" } as const;

/** Полоса прогресса: `value` из `max` (больше max — полная полоса). Подпись для экранных дикторов — `label`. */
export function Progress({ value, max = 100, tone = "primary", label, className }: { value: number; max?: number; tone?: keyof typeof progressTone; label?: string; className?: string }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={Math.min(Math.max(value, 0), max)} className={cn("h-2 w-full overflow-hidden rounded-full bg-surface-2", className)}>
      <div className={cn("h-full rounded-full transition-[width] duration-500 motion-reduce:transition-none", progressTone[tone])} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn("animate-pulse rounded-tile bg-surface-2", className)} />;
}

export function EmptyState({ icon, title, description, action, className }: { icon?: ReactNode; title: ReactNode; description?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-3 px-4 py-12 text-center", className)}>
      {icon && <div className="grid size-14 place-items-center rounded-full bg-surface-2 text-muted [&_svg]:size-6">{icon}</div>}
      <div>
        <h2 className="font-semibold">{title}</h2>
        {description && <p className="mt-1 max-w-sm text-sm text-muted">{description}</p>}
      </div>
      {action && <div className="mt-1 flex max-w-full flex-wrap justify-center gap-2">{action}</div>}
    </div>
  );
}

const alertTone = {
  info: ["bg-info-soft text-info", <Info key="i" />],
  success: ["bg-success-soft text-success", <CheckCircle2 key="s" />],
  warning: ["bg-warning-soft text-warning", <AlertTriangle key="w" />],
  danger: ["bg-danger-soft text-danger", <OctagonAlert key="d" />],
} as const;

/** Inline-сообщение на странице/в форме. */
export function Alert({ tone = "info", title, children, action, className }: { tone?: keyof typeof alertTone; title?: ReactNode; children?: ReactNode; action?: ReactNode; className?: string }) {
  const [cls, icon] = alertTone[tone];
  return (
    <div role={tone === "danger" ? "alert" : "status"} className={cn("flex gap-3 rounded-tile p-3 text-sm [&>svg]:mt-0.5 [&>svg]:size-4 [&>svg]:shrink-0", cls, className)}>
      {icon}
      <div className="min-w-0 flex-1 text-fg">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className={cn(title && "mt-0.5", "text-fg/80")}>{children}</div>}
      </div>
      {action}
    </div>
  );
}
